import { createTheme, responsiveFontSizes } from '@mui/material/styles';

/**
 * Workflow.Estate · Calm Professional design tokens (Direction A).
 *
 * Palette: warm-white surface, charcoal ink, blue accent (aligned with favicon).
 * Type:    Manrope for display/headings, Inter for UI/body.
 * Shape:   8px radius across buttons, inputs and cards.
 * Density: tighter line-heights, MUI defaults respected so existing
 *          pages keep working without a refactor.
 */

// ─── Color tokens ────────────────────────────────────────────────
export const palette = {
  // Surfaces
  bg: '#fafaf7',           // page background — warm white
  surface: '#ffffff',
  surfaceMuted: '#f5f3ec', // hover / subtle fills

  // Ink
  ink: '#14171c',
  inkMuted: '#5b6472',
  inkSubtle: '#8b8f9a',

  // Lines
  line: '#e8e6df',
  line2: '#d9d6cc',

  // Accent — same family as `public/favicon.svg` (blue 600 → 400)
  accent: '#1976d2',
  accentDark: '#1565c0',
  accentLight: '#42a5f5',
  accentSoft: '#e3f2fd',

  // Status (success stays green for semantics, distinct from brand primary)
  warn: '#b45309',
  warnSoft: '#fef3c7',
  ok: '#2e7d32',
  okSoft: '#e8f5e9',
  err: '#b91c1c',
  errSoft: '#fee2e2',
} as const;

const FONT_DISPLAY = '"Manrope", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_BODY = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const FONT_MONO = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

export { FONT_DISPLAY, FONT_BODY, FONT_MONO };

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: palette.accent,
      light: palette.accentLight,
      dark: palette.accentDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: palette.ink,
      light: palette.inkMuted,
      dark: '#000000',
      contrastText: '#ffffff',
    },
    background: {
      default: palette.bg,
      paper: palette.surface,
    },
    text: {
      primary: palette.ink,
      secondary: palette.inkMuted,
      disabled: palette.inkSubtle,
    },
    divider: palette.line,
    warning: { main: palette.warn, light: palette.warnSoft, contrastText: '#ffffff' },
    success: { main: palette.ok, light: palette.okSoft, contrastText: '#ffffff' },
    error:   { main: palette.err, light: palette.errSoft, contrastText: '#ffffff' },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: FONT_BODY,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '2.5rem', letterSpacing: '-0.025em', lineHeight: 1.1 },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '2rem',   letterSpacing: '-0.02em',  lineHeight: 1.15 },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.625rem', letterSpacing: '-0.015em', lineHeight: 1.2 },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.375rem', letterSpacing: '-0.01em',  lineHeight: 1.25 },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.005em', lineHeight: 1.3 },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1rem',     lineHeight: 1.35 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: '0.005em' },
    overline: { fontFamily: FONT_MONO, letterSpacing: '0.08em', fontWeight: 600, fontSize: '0.7rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.bg,
          color: palette.ink,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          paddingInline: 14,
        },
        containedPrimary: {
          '&:hover': { backgroundColor: palette.accentDark },
        },
        outlined: {
          borderColor: palette.line2,
          color: palette.ink,
          '&:hover': { backgroundColor: palette.surfaceMuted, borderColor: palette.line2 },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& fieldset': { borderColor: palette.line2 },
          '&:hover fieldset': { borderColor: palette.inkMuted },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${palette.line}`,
          boxShadow: 'none',
          backgroundColor: palette.surface,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { paddingBottom: 8 },
        title: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1rem' },
        subheader: { fontSize: '0.75rem', color: palette.inkMuted },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: palette.line },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: palette.bg,
          color: palette.ink,
          borderBottom: `1px solid ${palette.line}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.surface,
          borderRight: `1px solid ${palette.line}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          marginBlock: 2,
          '&.Mui-selected': {
            backgroundColor: palette.accentSoft,
            color: palette.accent,
            '& .MuiListItemIcon-root': { color: palette.accent },
            '&:hover': { backgroundColor: palette.accentSoft },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.ink,
          fontSize: 12,
          paddingInline: 10,
          paddingBlock: 6,
          borderRadius: 6,
        },
        arrow: { color: palette.ink },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${palette.line}` },
        head: {
          fontFamily: FONT_MONO,
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: palette.inkMuted,
          fontWeight: 600,
          backgroundColor: palette.bg,
        },
      },
    },
  },
});

export const theme = responsiveFontSizes(baseTheme);
export default theme;
